'use client'

import { useState, KeyboardEvent } from 'react'

import { Cell, Spacer, Text } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { KeyboardKey, onA11yKeyDown } from '@marketplace-web/vendor-abstractions/web-ui-util'
import { orderDetailsProtectionPoliciesClickEvent } from '@marketplace-web/seller-protection/seller-protection-data'

import SellerProtectionInformationDialog from '../../SellerProtectionInformationDialog'

type Props = {
  transactionId: number | undefined
  transactionStatus: number | null | undefined
  shipmentStatus: number | null | undefined
}

const ConversationDetailsSellerProtection = ({
  transactionId,
  transactionStatus,
  shipmentStatus,
}: Props) => {
  const translate = useTranslate('seller_protection_policies')
  const { track } = useTracking()

  const [isDialogVisible, setIsDialogVisible] = useState(false)

  const trackSellerProtectionClick = () => {
    if (!transactionId) return

    track(
      orderDetailsProtectionPoliciesClickEvent({
        screen: 'conversation_details',
        target: 'seller_protection_learn_more',
        transactionId: transactionId.toString(),
        details: JSON.stringify({
          transaction_status: transactionStatus,
          shipment_status: shipmentStatus,
        }),
      }),
    )
  }

  const openSellerProtectionDialog = () => {
    trackSellerProtectionClick()
    setIsDialogVisible(true)
  }

  const onKeyDown = (event: KeyboardEvent) => {
    onA11yKeyDown(
      event,
      { keys: [KeyboardKey.Enter, KeyboardKey.Space] },
      () => openSellerProtectionDialog,
    )
  }

  return (
    <>
      <Cell
        title={<Text text={translate('title')} type="section-heading" as="span" />}
        body={
          <>
            <Spacer size="medium" />
            <div className="member-protection-policy_list">
              <Text text={translate('description')} as="span" html />
            </div>
            <Spacer />
            <button
              type="button"
              tabIndex={0}
              onKeyDown={onKeyDown}
              onClick={openSellerProtectionDialog}
            >
              <Text text={translate('learn_more')} as="span" clickable />
            </button>
          </>
        }
        divider
      />
      <SellerProtectionInformationDialog
        transactionId={transactionId?.toString()}
        transactionStatus={transactionStatus}
        shipmentStatus={shipmentStatus}
        isVisible={isDialogVisible}
        onClose={() => setIsDialogVisible(false)}
      />
    </>
  )
}

export default ConversationDetailsSellerProtection
